import { useState } from 'react';
import {
  Heading,
  HStack,
  Pressable,
  Tabs,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Box, Separator } from '@chakra-ui/react';
import { FileText, X } from 'lucide-react';
import { LabOrder } from '../../../api/types';
import { LabStatusChip } from './LabStatusChip';
import { formatDate } from '../utils';
import { Loader } from '../../Loader';
import { useFetchDocument } from '../../../api/healthCoach/useFetchDocument';

type LabOrderDrawerContentProps = {
  labOrder: LabOrder;
  userId: string;
  onClose: () => void;
};

type DocumentEntry = {
  label: string;
  documentId: number;
};

const buildDocumentEntries = (labOrder: LabOrder): DocumentEntry[] => {
  const entries: DocumentEntry[] = [];

  if (labOrder.requisition_document_id) {
    entries.push({
      label: 'Requisition',
      documentId: labOrder.requisition_document_id,
    });
  }

  if (labOrder.lab_result_document_id) {
    entries.push({
      label: 'Result',
      documentId: labOrder.lab_result_document_id,
    });
  }

  return entries;
};

const DocumentViewer = ({
  userId,
  documentId,
  label,
}: {
  userId: string;
  documentId: number;
  label: string;
}) => {
  const { objectUrl, isLoading, error } = useFetchDocument(
    userId,
    documentId
  );

  if (error) {
    return (
      <VStack h={'100%'} justify={'center'} align={'center'} gap={1} p={6}>
        <Text fontWeight={600} color={'red.500'}>
          {error.message}
        </Text>
      </VStack>
    );
  }

  if (isLoading || !objectUrl) {
    return (
      <VStack h={'100%'} justify={'center'} align={'center'}>
        <Loader loading />
      </VStack>
    );
  }

  return (
    <iframe
      key={objectUrl}
      src={objectUrl}
      title={label}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
        background: 'white',
      }}
    />
  );
};

export const LabOrderDrawerContent = ({
  labOrder,
  userId,
  onClose,
}: LabOrderDrawerContentProps) => {
  const documentEntries = buildDocumentEntries(labOrder);
  // Results are appended after the requisition, so the last entry is the
  // most recent one - default to it rather than the requisition tab.
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(documentEntries.length - 1, 0)
  );

  const eyebrow = [
    labOrder.lab_company,
    formatDate(labOrder.created_at ?? undefined, 'MMM D, YYYY'),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <VStack h={'100%'} w={'100%'} align={'stretch'} gap={0}>
      <VStack align={'stretch'} gap={3} px={6} pt={5} pb={4}>
        <HStack justify={'space-between'} align={'flex-start'} gap={4}>
          <VStack align={'flex-start'} gap={1}>
            <Text
              fontSize={'2xs'}
              fontWeight={700}
              color={'bluegrey'}
              textTransform={'uppercase'}
              letterSpacing={'0.04em'}
            >
              {eyebrow}
            </Text>
            <Heading size={'md'} color={'secondary.500'}>
              {labOrder.lab}
            </Heading>
          </VStack>
          <Pressable onClick={onClose}>
            <X size={20} />
          </Pressable>
        </HStack>

        <HStack justify={'space-between'} align={'center'} gap={4} w={'100%'}>
          {labOrder.result_status ? (
            <LabStatusChip status={labOrder.result_status} />
          ) : (
            <Box />
          )}
          {!!labOrder.orderer?.name && (
            <Text fontSize={'xs'} color={'bluegrey'}>
              Ordered by {labOrder.orderer.name}
            </Text>
          )}
        </HStack>

        {documentEntries.length === 1 && (
          <HStack gap={2} align={'center'} color={'bluegrey'}>
            <FileText size={14} />
            <Text fontSize={'xs'} color={'secondary.500'} lineClamp={1}>
              {documentEntries[0].label}
            </Text>
          </HStack>
        )}
      </VStack>

      <Separator />

      <Box flex={1} minH={0} w={'100%'} bg={'cloud.50'}>
        {documentEntries.length === 0 ? (
          <VStack h={'100%'} justify={'center'} align={'center'} gap={1} p={6}>
            <Text fontWeight={600} color={'secondary.500'}>
              No document to preview
            </Text>
            <Text fontSize={'sm'} color={'bluegrey'} textAlign={'center'}>
              This lab has no associated documents.
            </Text>
          </VStack>
        ) : documentEntries.length === 1 ? (
          <DocumentViewer
            userId={userId}
            documentId={documentEntries[0].documentId}
            label={documentEntries[0].label}
          />
        ) : (
          <Tabs
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            tabs={documentEntries.map((entry) => ({
              label: entry.label,
              content: (
                <DocumentViewer
                  userId={userId}
                  documentId={entry.documentId}
                  label={entry.label}
                />
              ),
            }))}
          />
        )}
      </Box>
    </VStack>
  );
};

export default LabOrderDrawerContent;
